import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download, Trash2, RefreshCw, Users, Search, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMiniMakers = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/admin/mini-makers-waitlist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEntries(res.data);
    } catch {
      toast.error("Failed to load waitlist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEntries(); }, []);

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/admin/mini-makers-waitlist/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mini-makers-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/admin/mini-makers-waitlist/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEntries(prev => prev.filter(e => e.id !== id));
      setDeleteId(null);
      toast.success("Entry deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = entries.filter(e => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      e.parent_first_name?.toLowerCase().includes(q) ||
      e.parent_last_name?.toLowerCase().includes(q) ||
      e.email?.toLowerCase().includes(q) ||
      e.child_first_name?.toLowerCase().includes(q)
    );
  });

  return (
    <AdminLayout>
    <div data-testid="admin-mini-makers-page">
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-['Playfair_Display'] text-2xl text-[#F5F5F0]">Mini Makers Waitlist</h1>
            <p className="text-[#52525B] text-sm">{entries.length} total signups</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchEntries} className="flex items-center gap-2 px-3 py-2 text-sm text-[#A1A1AA] hover:text-[#D4AF37] border border-[rgba(212,175,55,0.15)] hover:border-[rgba(212,175,55,0.4)] transition-colors" data-testid="refresh-waitlist">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#050505] text-sm font-semibold hover:bg-[#F7E7CE] transition-colors" data-testid="export-csv-btn">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>
        </div>
      </div>

      <div>
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
          <input
            type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search by name, email, or child's name..."
            className="w-full bg-[#121212] border border-[rgba(212,175,55,0.15)] text-[#F5F5F0] pl-10 pr-4 py-3 text-sm focus:border-[#D4AF37] focus:outline-none"
            data-testid="waitlist-search"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-[#52525B]">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg">No waitlist entries yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="waitlist-table">
              <thead>
                <tr className="border-b border-[rgba(212,175,55,0.15)]">
                  {["Parent Name", "Email", "Phone", "# Kids", "Children (name · age)", "Location", "Signed Up", "Actions"].map(h => (
                    <th key={h} className="text-left text-[#52525B] text-xs uppercase tracking-wider py-3 px-3 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(entry => {
                  const kids = Array.isArray(entry.children) && entry.children.length > 0
                    ? entry.children.filter(c => c?.first_name)
                    : (entry.child_first_name
                        ? [{ first_name: entry.child_first_name, age: entry.child_age || "" }]
                        : []);
                  return (
                    <tr key={entry.id} className="border-b border-[rgba(212,175,55,0.08)] hover:bg-[#0F0F0F] transition-colors" data-testid={`waitlist-row-${entry.id}`}>
                      <td className="py-3 px-3">
                        <p className="text-[#F5F5F0] text-sm font-semibold">{entry.parent_first_name} {entry.parent_last_name}</p>
                      </td>
                      <td className="py-3 px-3 text-[#A1A1AA] text-sm">{entry.email}</td>
                      <td className="py-3 px-3 text-[#A1A1AA] text-sm">{entry.phone || "--"}</td>
                      <td className="py-3 px-3 text-[#A1A1AA] text-sm">{entry.num_children || kids.length || "--"}</td>
                      <td className="py-3 px-3" data-testid={`waitlist-children-${entry.id}`}>
                        {kids.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {kids.map((c, i) => (
                              <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-[rgba(212,175,55,0.1)] border border-[rgba(212,175,55,0.25)] text-[#D4AF37] font-medium">
                                {c.first_name}{c.age ? ` · ${c.age}` : ""}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[#52525B] text-xs">--</span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-[#A1A1AA] text-sm max-w-[140px] truncate">{entry.preferred_location || "--"}</td>
                      <td className="py-3 px-3 text-[#52525B] text-xs">{new Date(entry.created_at).toLocaleDateString("en-CA")}</td>
                      <td className="py-3 px-3">
                        <button onClick={() => setDeleteId(entry.id)} className="p-1.5 text-[#A1A1AA] hover:text-red-400 transition-colors" data-testid={`delete-entry-${entry.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setDeleteId(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#121212] border border-[rgba(212,175,55,0.3)] p-6 max-w-sm w-full" onClick={e => e.stopPropagation()} data-testid="delete-confirm-modal">
            <h3 className="font-['Playfair_Display'] text-lg text-[#F5F5F0] mb-3">Delete Entry?</h3>
            <p className="text-[#A1A1AA] text-sm mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2 border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] text-sm hover:text-[#F5F5F0]">Cancel</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 py-2 bg-red-500/20 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/30" data-testid="confirm-delete-btn">Delete</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminMiniMakers;
